import { DynamicRequestForm, type TemplateField, type UserOption } from '@/components/features/ServiceRequests/DynamicRequestForm';
import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/shadcn/button';
import { Label } from '@/components/ui/shadcn/label';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type PageProps } from '@/types';
import { Head, useForm, usePage } from '@inertiajs/react';
import { useState } from 'react';
import { route } from 'ziggy-js';

interface CreatePageProps extends PageProps {
    [key: string]: unknown;
    template: {
        id: string;
        slug: string;
        name: string;
        description: string | null;
        fields: TemplateField[];
        subject_field_key: string | null;
    };
    users: UserOption[];
}

export default function ServiceRequestCreatePage() {
    const { props } = usePage<CreatePageProps>();
    const { template, users } = props;
    const { t } = useTranslations();

    const authUserId = props.auth?.user?.id ?? null;

    // The departing employee usually initiates their own clearance, so pre-select
    // the logged-in user into the template's designated "subject" field.
    const [submittedData, setSubmittedData] = useState<Record<string, unknown>>(() =>
        template.subject_field_key && authUserId ? { [template.subject_field_key]: authUserId } : {},
    );
    const { data, setData, transform, post, processing, errors } = useForm({
        template_id: template.id,
        requester_notes: '',
    });

    transform((d) => ({ ...d, submitted_data: submittedData }));

    const breadcrumbs = [
        { title: t('service_requests.title', 'Service Requests'), href: '/service-requests' },
        { title: t('service_requests.catalog_title', 'Submit a Request'), href: '/service-requests/catalog' },
        { title: template.name, href: '#' },
    ];

    const handleFieldChange = (key: string, value: unknown) => {
        setSubmittedData((prev) => ({ ...prev, [key]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        post(route('service-requests.store'));
    };

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={template.name} />

            <PageHeader title={template.name} subtitle={template.description ?? ''} />

            <PageContent>
                <form onSubmit={handleSubmit} className="w-full space-y-6">
                    <div className="bg-card rounded-xl border p-6">
                        <h2 className="mb-5 text-sm font-semibold tracking-wide uppercase">
                            {t('service_requests.submitted_data', 'Request Details')}
                        </h2>
                        <DynamicRequestForm
                            fields={template.fields}
                            data={submittedData}
                            onChange={handleFieldChange}
                            errors={errors as Record<string, string>}
                            users={users}
                        />
                    </div>

                    <div className="bg-card rounded-xl border p-6">
                        <div className="space-y-1.5">
                            <Label htmlFor="requester_notes" className="text-sm font-medium">
                                {t('service_requests.requester_notes', 'Additional notes')}
                            </Label>
                            <Textarea
                                id="requester_notes"
                                value={data.requester_notes}
                                onChange={(e) => setData('requester_notes', e.target.value)}
                                rows={3}
                                placeholder={t('service_requests.form.notes_placeholder', 'Any additional context…')}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <Button type="submit" disabled={processing}>
                            {processing ? t('service_requests.submitting', 'Submitting...') : t('service_requests.submit_request', 'Submit Request')}
                        </Button>
                    </div>
                </form>
            </PageContent>
        </AppLayout>
    );
}
